import { Loading, Panel } from "@appkit4/react-components";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CompanyPanel from "../../components/CompanyPanel";
import ConsolidatedPartnerships from "../../components/ConsolidatedPartnerships/ConsolidatedPartnerships";
import {
  deriveConsolidatedPartnershipElection,
  normalizeConsolidatedPartnerships,
  validateConsolidatedPartnerships,
} from "../../components/ConsolidatedPartnerships/consolidatedPartnershipUtils";
import CustomTooltipIcon from "../../components/customTooltipIcon/customTooltipIcon";
import toast from "../../components/DismissibleToast";
import { BackButton, NextButton } from "../../components/ProgressNav";
import {
  Button,
  CalendarPicker,
  Input,
  Switch,
} from "../../components/ReadonlyAwareInputs";
import { AuthContext } from "../../services/AuthProvider";
import {
  canProgressCalculation,
  disableProgressInViewMode,
  getCurrentPeriodIsReadonly,
  getGroupData,
  getPageTooltips,
  getPoaIndex,
  getSelectedPeriod,
  setGroupData,
} from "../../services/GroupContext";
import sqlService from "../../services/sqldatabase/sqldatabase.service";
import { processDate, processDateJS } from "../../utils/dateProcessor";
import { formatCurrency } from "../../utils/formatters";
import { migrateDisregardRegulationElection } from "../../utils/migrationScripts";
import ANGIE from "./ANGIE";
import EBITDA from "./EBITDA";
import "./groupData.scss";
import NGIE from "./NGIE";
import QNGIE from "./QNGIE";
dayjs.extend(utc);

const GroupData = () => {
  const selectedPeriod = getSelectedPeriod();
  const { period_start, period_end } = selectedPeriod;
  const poaIndex = getPoaIndex();

  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isPosting, setIsPosting] = useState(false);

  const [showNGIE, setShowNGIE] = useState(false);
  const [NGIEError, setNGIEError] = useState(false);
  const [showANGIE, setShowANGIE] = useState(false);
  const [ANGIEError, setANGIEError] = useState(false);
  const [showQNGIE, setShowQNGIE] = useState(false);
  const [QNGIEError, setQNGIEError] = useState(false);
  const [showEBITDA, setShowEBITDA] = useState(false);
  const [EBITDAError, setEBITDAError] = useState(false);

  const [angieEstimated, setAngieEstimated] = useState();

  useEffect(() => {
    //Check firstly that the is a group data.
    if (getGroupData()) {
      //Set the value of the checkbox
      setAngieEstimated(
        getGroupData().periods_of_account[poaIndex].angie_estimated,
      );
    }
  }, []);

  const [showGR, setShowGR] = useState(false);
  const [interestAllowanceElection, setInterestAllowanceElection] =
    useState(false);
  const [showGRBE, setShowGRBE] = useState(false);

  const [isError, setIsError] = useState(false);

  const [investorTable, setInvestorTable] = useState([
    {
      investor_group: "",
      blended_group_ratio: "",
      blended_group_ebitda: "",
    },
  ]);
  const [duplicateInvestorErrors, setDuplicateInvestorErrors] = useState([]);

  const [investmentsTable, setInvestmentsTable] = useState([
    {
      investment_name: "",
    },
  ]);
  const [duplicateInvestmentsErrors, setDuplicateInvestmentsErrors] = useState(
    [],
  );

  const [periodData, setPeriodData] = useState();
  const [periodIndex, setPeriodIndex] = useState();

  const [blendedGroupRatio, setBlendedGroupRatio] = useState();
  const [blendedNetGroupInterestExpense, setBlendedNetGroupInterestExpense] =
    useState(0);

  const [electionConsolidatedIAS, setElectionConsolidatedIAS] = useState(false);
  const [electionConsolidatedIASDate, setElectionConsolidatedIASDate] =
    useState();
  const [electionConsolidatedIASLink, setElectionConsolidatedIASLink] =
    useState();
  const [electionConsolidatedIASMaxDate, setElectionConsolidatedIASMaxDate] =
    useState(false);

  const [electionNoStatementsDrawnUp, setElectionNoStatementsDrawnUp] =
    useState(false);
  const [electionNoStatementsDrawnUpDate, setElectionNoStatementsDrawnUpDate] =
    useState();
  const [electionNoStatementsDrawnUpLink, setElectionNoStatementsDrawnUpLink] =
    useState();
  const [
    electionNoStatementsDrawnUpMaxDate,
    setElectionNoStatementsDrawnUpMaxDate,
  ] = useState(false);
  const [recordedElectionsValidators, setRecordedElectionsValidators] =
    useState({ s484: true, s486: true });
  const [irrevocableElections, setIrrevocableElections] = useState({});
  const [consolidatedPartnershipsError, setConsolidatedPartnershipsError] =
    useState(false);
  const [
    consolidatedPartnershipsStartDateError,
    setConsolidatedPartnershipsStartDateError,
  ] = useState(false);
  const [applyGroupRatioElection, setApplyGroupRatioElection] = useState(
    selectedPeriod?.apply_group_ratio_election,
  );

  const isReadOnly = auth?.isReadOnlyUser();
  const disableViewModeProgress = disableProgressInViewMode();
  const tooltips = useMemo(() => getPageTooltips("groupData"), []);

  useEffect(() => {
    setIsError(
      Object.values(recordedElectionsValidators)?.some(
        (value) => value === false,
      ) ||
        duplicateInvestorErrors?.some((value) => value === true) ||
        duplicateInvestmentsErrors.some((value) => value === true) ||
        NGIEError ||
        ANGIEError ||
        QNGIEError ||
        EBITDAError ||
        consolidatedPartnershipsError ||
        consolidatedPartnershipsStartDateError,
    );
  }, [
    recordedElectionsValidators?.s484,
    recordedElectionsValidators?.s486,
    duplicateInvestorErrors,
    duplicateInvestmentsErrors,
    NGIEError,
    ANGIEError,
    QNGIEError,
    EBITDAError,
    consolidatedPartnershipsError,
    consolidatedPartnershipsStartDateError,
  ]);

  // Validate consolidated partnerships whenever they change
  useEffect(() => {
    if (irrevocableElections?.consolidatedPartnership?.switch) {
      const groupData = getGroupData();
      const validation = validateConsolidatedPartnerships({
        partnerships:
          irrevocableElections?.consolidatedPartnership?.partnerships ||
          groupData?.consolidated_partnerships ||
          [],
        poaPeriodEndDates:
          groupData?.periods_of_account
            ?.map((poa) => poa?.period_end)
            ?.filter((date) => !!date) || [],
        firstModelPeriodStartDate: groupData?.model_first_period_start,
        requireAtLeastOne: true,
        requireUniqueNames: true,
      });

      setConsolidatedPartnershipsStartDateError(validation.hasInvalidStartDate);
      setConsolidatedPartnershipsError(validation.hasErrors);
    } else {
      setConsolidatedPartnershipsError(false);
      setConsolidatedPartnershipsStartDateError(false);
    }
  }, [irrevocableElections?.consolidatedPartnership]);

  const formatNumber = (value) => {
    if (!value) return ""; // Return empty string if value is null or undefined
    // Convert value to a number, add commas for thousands separator, and return as string
    return Number(value)?.toLocaleString("en-US");
  };

  // Function to parse formatted number back to a plain number
  const parseNumber = (value) => {
    // Remove commas from the value and return as a plain number
    return parseFloat(value?.replace(/,/g, ""));
  };
  const handleInputChange = (value) => {
    // Parse the value and set the state
    setBlendedNetGroupInterestExpense(parseNumber(value));
  };

  useEffect(() => {
    let savedData = getGroupData();

    if (!savedData) {
      toast.error("Failed get to get group data...");
      return;
    }

    const allPeriods = savedData?.periods_of_account;
    if (!allPeriods) {
      navigate("/app/grouppoa");
      toast.error("No period of accounts have been set up yet!");
      return;
    }

    if (!selectedPeriod) {
      setPeriodData(null);
      setPeriodIndex(null);
      return;
    }

    // Apply Migration (Azure Card: 8916)
    savedData = migrateDisregardRegulationElection(savedData);
    setGroupData(savedData);

    setIrrevocableElections({
      disregardRegulations: {
        switch: savedData?.disregard_regulations_election ?? false,
        date: savedData?.disregard_regulations_election_year,
        comment: savedData?.disregard_regulation_comment,
        changed: false,
      },
      EBITDA: {
        switch: savedData?.group_ebitda_gains,
        date: savedData?.group_ebitda_gains_year,
        comment: savedData?.group_ebitda_gains_comment,
        changed: false,
      },
      alternativeCalculation: {
        switch: savedData?.interest_allowance_alt_calc,
        date: savedData?.interest_allowance_alt_calc_year,
        comment: savedData?.interest_allowance_alt_calc_comment,
        changed: false,
      },
      consolidatedPartnership: {
        switch: savedData?.interest_allowance_consolidated_partnership,
        date: savedData?.interest_allowance_consolidated_partnership_year,
        comment: savedData?.interest_allowance_consolidated_partnership_comment,
        changed: false,
      },
    });

    if (selectedPeriod?.period_start && selectedPeriod?.period_end) {
      for (let index = 0; index < allPeriods?.length; index++) {
        const searchingPeriod = allPeriods[index];
        if (
          processDateJS(searchingPeriod?.period_start)?.isSame(
            processDateJS(selectedPeriod?.period_start),
            "day",
          ) &&
          processDateJS(searchingPeriod?.period_end)?.isSame(
            processDateJS(selectedPeriod?.period_end),
            "day",
          )
        ) {
          searchingPeriod.fx_rate = searchingPeriod?.fx_rate ?? 1;

          setPeriodData(searchingPeriod);
          setInterestAllowanceElection(
            searchingPeriod.election_interest_allowance_non_consolidated_investment,
          );
          setPeriodIndex(index);
          setShowGR(searchingPeriod?.group_ratio_election);
          setShowGRBE(searchingPeriod?.group_ratio_blended_election);
          setBlendedGroupRatio(searchingPeriod?.blended_group_ratio);
          setBlendedNetGroupInterestExpense(
            searchingPeriod?.blended_net_group_interest_expense,
          );
          setElectionConsolidatedIAS(
            searchingPeriod?.election_consolidated_ias,
          );
          setElectionConsolidatedIASDate(
            processDateJS(searchingPeriod?.election_consolidated_ias_date),
          );
          setElectionConsolidatedIASLink(
            searchingPeriod?.election_consolidated_ias_link,
          );
          setElectionNoStatementsDrawnUp(
            searchingPeriod?.election_no_statements_drawn_up,
          );
          setElectionNoStatementsDrawnUpDate(
            processDateJS(
              searchingPeriod?.election_no_statements_drawn_up_date,
            ),
          );
          setElectionNoStatementsDrawnUpLink(
            searchingPeriod?.election_no_statements_drawn_up_link,
          );
          if (
            searchingPeriod?.hasOwnProperty("investor_group_data") &&
            searchingPeriod?.investor_group_data.length > 0
          ) {
            setInvestorTable(searchingPeriod?.investor_group_data);
          }
          if (
            searchingPeriod?.hasOwnProperty("investments") &&
            searchingPeriod?.investments.length > 0
          ) {
            setInvestmentsTable(searchingPeriod?.investments);
          }
          const periodEndDate = processDateJS(searchingPeriod?.period_end);
          setElectionConsolidatedIASMaxDate(periodEndDate);
          setElectionNoStatementsDrawnUpMaxDate(periodEndDate);

          return;
        }
      }

      toast.error(
        `Failed to find period data for the period: ${selectedPeriod?.period_start} : ${selectedPeriod?.period_end}`,
      );
    }
  }, []);

  const save = (newPeriodData = {}) => {
    let savedData = getGroupData();

    if (!savedData) {
      throw "Failed get to get group data...";
    }

    // Let's find the PoA we're trying to update
    const allPeriods = savedData?.periods_of_account;
    if (!allPeriods) {
      throw "Failed get to get group period of accounts...";
    }

    setIsPosting(true);

    if (isReadOnly || getCurrentPeriodIsReadonly()) {
      return;
    }

    let period = { ...periodData, ...newPeriodData };
    period.election_consolidated_ias = electionConsolidatedIAS;
    period.election_consolidated_ias_date = electionConsolidatedIASDate;
    period.election_consolidated_ias_link = electionConsolidatedIASLink;
    period.election_no_statements_drawn_up = electionNoStatementsDrawnUp;
    period.election_no_statements_drawn_up_date =
      electionNoStatementsDrawnUpDate;
    period.election_no_statements_drawn_up_link =
      electionNoStatementsDrawnUpLink;
    period.apply_group_ratio_election = applyGroupRatioElection;

    if (showGR != null) {
      period.group_ratio_election = showGR;
      if (showGRBE != null) {
        period.group_ratio_blended_election = showGRBE;
        if (showGRBE) {
          period.investor_group_data = investorTable;
          period.blended_group_ratio = blendedGroupRatio;
          period.blended_net_group_interest_expense =
            blendedNetGroupInterestExpense;
        }
      }
    }
    period.election_interest_allowance_non_consolidated_investment =
      interestAllowanceElection;
    if (interestAllowanceElection) {
      period.investments = investmentsTable;
    }

    setPeriodData(period);
    allPeriods[periodIndex] = period;

    const canProgressCalc = canProgressCalculation();

    let finalAllPeriods = allPeriods.map((period, index) =>
      index === poaIndex
        ? {
            ...period,
            angie_estimated: angieEstimated,
            ...canProgressCalc,
          }
        : period,
    );

    savedData.periods_of_account = finalAllPeriods;

    // Auto-calculate consolidated partnership election based on start dates
    // Only if user hasn't manually changed the election toggle
    const partnerships =
      irrevocableElections?.consolidatedPartnership?.partnerships ||
      savedData.consolidated_partnerships ||
      [];

    const consolidatedPartnershipValidation = validateConsolidatedPartnerships({
      partnerships,
      poaPeriodEndDates:
        savedData?.periods_of_account
          ?.map((poa) => poa?.period_end)
          ?.filter((date) => !!date) || [],
      firstModelPeriodStartDate: savedData?.model_first_period_start,
      requireAtLeastOne: false,
      requireUniqueNames: true,
    });

    if (consolidatedPartnershipValidation.hasInvalidStartDate) {
      setConsolidatedPartnershipsError(true);
      setConsolidatedPartnershipsStartDateError(true);
      setIsPosting(false);
      toast.error(
        "Invalid consolidated partnership start date. Use a PoA period end date or a date before the first model PoA start date.",
      );
      return;
    }

    if (consolidatedPartnershipValidation.hasDuplicateNames) {
      setConsolidatedPartnershipsError(true);
      setConsolidatedPartnershipsStartDateError(false);
      setIsPosting(false);
      toast.error("Duplicate consolidated partnership names are not allowed.");
      return;
    }

    const { switchValue: autoCalculatedSwitch, yearValue: autoCalculatedYear } =
      deriveConsolidatedPartnershipElection(partnerships, period_end);

    savedData = {
      ...savedData,
      disregard_regulations_election:
        irrevocableElections?.disregardRegulations?.switch,
      disregard_regulations_election_year:
        irrevocableElections?.disregardRegulations?.date,
      disregard_regulation_comment:
        irrevocableElections?.disregardRegulations?.comment,

      group_ebitda_gains: irrevocableElections?.EBITDA?.switch,
      group_ebitda_gains_year: irrevocableElections?.EBITDA?.date,
      group_ebitda_gains_comment: irrevocableElections?.EBITDA?.comment,

      interest_allowance_alt_calc:
        irrevocableElections?.alternativeCalculation?.switch,
      interest_allowance_alt_calc_year:
        irrevocableElections?.alternativeCalculation?.date,
      interest_allowance_alt_calc_comment:
        irrevocableElections?.alternativeCalculation?.comment,

      // Auto-calculate consolidated partnership election based on partnerships
      // No manual override - always use calculated values from partnership data
      interest_allowance_consolidated_partnership: autoCalculatedSwitch,
      interest_allowance_consolidated_partnership_year: autoCalculatedYear,
      interest_allowance_consolidated_partnership_comment:
        irrevocableElections?.consolidatedPartnership?.comment,
    };

    // Save consolidated partnerships data if it has been changed
    if (irrevocableElections?.consolidatedPartnership?.changed) {
      // Normalize to ensure all required fields exist with proper defaults
      savedData.consolidated_partnerships = normalizeConsolidatedPartnerships(
        irrevocableElections.consolidatedPartnership.partnerships,
      );
    }

    const promise = sqlService?.postResponse(savedData);

    const onSuccess = () => {
      setGroupData(savedData);
      setIsPosting(false);
    };
    return toast.save(promise, onSuccess, () => setIsPosting(false));
  };

  useEffect(() => {
    if (!periodData) {
      return;
    }
  }, [periodData]);
  useEffect(() => {
    setDuplicateInvestorErrors(
      investorTable?.map((outerV, outerI) =>
        investorTable?.some(
          (innerV, innerI) =>
            outerV["investor_group"] === innerV["investor_group"] &&
            outerI !== innerI,
        ),
      ),
    );
  }, [investorTable]);

  useEffect(() => {
    const uniqueInvestmentsCount = investmentsTable.reduce(
      (count, investment) => {
        count[investment["investment_name"]] =
          (count[investment["investment_name"]] || 0) + 1;
        return count;
      },
      {},
    );

    setDuplicateInvestmentsErrors(
      investmentsTable.map(
        (investment) =>
          uniqueInvestmentsCount[investment["investment_name"]] > 1,
      ),
    );
  }, [investmentsTable]);

  const addTableRows = (tableName) => {
    if (tableName === "investor") {
      setInvestorTable((prevState) => [
        ...prevState,
        {
          investor_group: "",
          blended_group_ratio: "",
          blended_group_ebitda: "",
        },
      ]);
    } else if (tableName === "investment") {
      setInvestmentsTable((prevState) => [
        ...prevState,
        {
          investment_name: "",
        },
      ]);
    }
  };

  const getTableData = (name) => {
    if (name === "investor_group") {
      return investorTable;
    } else if (name === "investment_name") {
      return investmentsTable;
    }
  };

  const getTableSetter = (name) => {
    if (name === "investor_group") {
      return setInvestorTable;
    } else if (name === "investment_name") {
      return setInvestmentsTable;
    }
  };

  const deleteTableRows = (e, index, name) => {
    const rows = [...getTableData(name)];
    rows?.splice(index, 1);
    const tableSetter = getTableSetter(name);
    tableSetter(rows);
  };

  const tableChange = (index, value, name) => {
    const rowsInput = [...getTableData(name)];
    rowsInput[index][name] = value;
    const tableSetter = getTableSetter(name);
    tableSetter(rowsInput);
  };

  if (isLoading) {
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      ></Loading>
    );
  }

  const handleGRToggle = (value) => {
    if (!value) {
      setShowGRBE(false);
      setApplyGroupRatioElection(false);

      if (!isElectionDisabled("group_ebitda_gains")) {
        setIrrevocableElections((state) => ({
          ...state,
          EBITDA: { ...state.EBITDA, switch: false },
        }));
      }
    }

    setShowGR(value);
  };

  const isYearNotAfterPeriodEnd = (electionName) => {
    const groupData = getGroupData();
    if (!groupData?.[`${electionName}_year`]) {
      return false;
    }
    return !processDateJS(groupData?.[`${electionName}_year`])?.isAfter(
      processDateJS(period_end),
    );
  };

  const isElectionDisabled = (electionName) => {
    const groupData = getGroupData();
    return (
      groupData?.[`${electionName}`] &&
      ((!processDateJS(groupData?.[`${electionName}_year`])?.isSame(
        processDateJS(period_end),
      ) &&
        isYearNotAfterPeriodEnd(electionName)) ||
        selectedPeriod.return_status.isSubmitted ||
        selectedPeriod.return_status.isRevised)
    );
  };

  const isElectionTurnedOn = (electionName) => {
    const groupData = getGroupData();
    const isElectionFlagOn =
      groupData?.[`${electionName}`] && isYearNotAfterPeriodEnd(electionName);

    // For consolidated partnerships, also check if partnerships exist with dates in or before current period
    if (electionName === "interest_allowance_consolidated_partnership") {
      const selectedPeriod = getSelectedPeriod();
      const consolidatedPartnerships =
        normalizeConsolidatedPartnerships(
          groupData?.consolidated_partnerships,
        ) || [];
      const hasPriorPartnership = consolidatedPartnerships.some(
        (cp) =>
          cp.date &&
          processDateJS(cp.date).isSameOrBefore(
            processDateJS(selectedPeriod?.period_end),
            "day",
          ),
      );
      return isElectionFlagOn || hasPriorPartnership;
    }

    return isElectionFlagOn;
  };

  return (
    <div className="ap-container">
      <CompanyPanel />
      {periodData && (
        <>
          <Panel className="mt-2" title="Group Level Information">
            <p className="mt-2">
              These buttons allow you to enter the group-level data required to
              complete the CIR calculations. This information is obtained from
              (actual or deemed) consolidated financial statements of the
              worldwide group. The income statement/profit and loss account
              should provide a useful starting point, but it will be necessary
              to review the notes to the financial statements and obtain
              further, more granular, information (e.g. from the extended trial
              balance) to make all the necessary adjustments.
            </p>
            <div className="row">
              <p className="col">NGIE - Net group-interest expense (income):</p>
              <p className="col">
                Total: GBP{" "}
                {periodData?.net_group_interest_expense
                  ? formatCurrency(
                      Math.round(periodData?.net_group_interest_expense),
                    )
                  : 0}
              </p>
              <Button
                viewEditButton
                neverReadonly
                className="col"
                onClick={() => {
                  setShowNGIE(true);
                }}
              />
              {showNGIE && (
                <NGIE
                  setVisible={setShowNGIE}
                  currentPeriod={periodData}
                  setCurrentPeriod={setPeriodData}
                  save={save}
                  errorState={[NGIEError, setNGIEError]}
                  isParentError={isError}
                  isReadOnly={isReadOnly}
                ></NGIE>
              )}
            </div>
            <div className="row">
              <p className="col">
                ANGIE - Adjusted net group-interest expense:
              </p>
              <p className="col">
                Total: GBP{" "}
                {periodData?.adjusted_net_group_interest_expense
                  ? formatCurrency(
                      Math.round(
                        periodData?.adjusted_net_group_interest_expense,
                      ),
                    )
                  : 0}{" "}
              </p>
              <Button
                viewEditButton
                neverReadonly
                className="col"
                onClick={() => {
                  setShowANGIE(true);
                }}
              />
              {showANGIE && (
                <ANGIE
                  setVisible={setShowANGIE}
                  currentPeriod={periodData}
                  setCurrentPeriod={setPeriodData}
                  save={save}
                  errorState={[ANGIEError, setANGIEError]}
                  isParentError={isError}
                  isReadOnly={isReadOnly}
                  angieEstimated={angieEstimated}
                  setAngieEstimated={setAngieEstimated}
                ></ANGIE>
              )}
            </div>
            <div className="row">
              <div className="d-flex flex-row col ap-pr-spacing-0">
                <p className="ap-mt-spacing-2">
                  Enable Group Ratio election calculation:
                </p>
                <CustomTooltipIcon
                  tooltipText={tooltips.enableGroupRatioCalculation}
                />
              </div>
              <div className="col" style={{ height: 40 }}>
                <Switch
                  style={{ paddingLeft: 0, width: "fit-content" }}
                  className="col"
                  showIndicator
                  defaultChecked={showGR}
                  value={showGR}
                  onChange={(value) => {
                    handleGRToggle(value);
                  }}
                />
              </div>
            </div>
            {showGR && (
              <div>
                <div className="row">
                  <p className="col">
                    QNGIE - Qualifying net group-interest expense:
                  </p>
                  <p className="col">
                    Total: GBP{" "}
                    {periodData?.qualifying_net_group_interest_expense
                      ? formatCurrency(
                          Math.round(
                            periodData?.qualifying_net_group_interest_expense,
                          ),
                        )
                      : 0}
                  </p>
                  <Button
                    viewEditButton
                    neverReadonly
                    className="col"
                    onClick={() => {
                      setShowQNGIE(true);
                    }}
                  />
                  {showQNGIE && (
                    <QNGIE
                      setVisible={setShowQNGIE}
                      currentPeriod={periodData}
                      setCurrentPeriod={setPeriodData}
                      save={save}
                      errorState={[QNGIEError, setQNGIEError]}
                      isParentError={isError}
                      isReadOnly={isReadOnly}
                    ></QNGIE>
                  )}
                </div>
                <div className="row">
                  <p className="col">Group EBITDA:</p>
                  <p className="col">
                    Total: GBP{" "}
                    {periodData?.group_ebitda
                      ? formatCurrency(Math.round(periodData?.group_ebitda))
                      : 0}{" "}
                  </p>
                  <Button
                    viewEditButton
                    neverReadonly
                    className="col"
                    onClick={() => {
                      setShowEBITDA(true);
                    }}
                  />
                  {showEBITDA && (
                    <EBITDA
                      setVisible={setShowEBITDA}
                      currentPeriod={periodData}
                      setCurrentPeriod={setPeriodData}
                      save={save}
                      errorState={[EBITDAError, setEBITDAError]}
                      isParentError={isError}
                      isReadOnly={isReadOnly}
                    ></EBITDA>
                  )}
                </div>
              </div>
            )}
            {showGR && (
              <div className="row">
                <div className="d-flex flex-row col ap-pr-spacing-0">
                  <p className="">Group ratio - blended election:</p>
                  <CustomTooltipIcon
                    tooltipText={tooltips.groupRatioBlendedElection}
                  />
                </div>
                <div className="col" style={{ height: 40 }}>
                  <Switch
                    style={{ paddingLeft: 0, width: "fit-content" }}
                    className="col"
                    showIndicator
                    defaultChecked={showGRBE}
                    onChange={(value) => {
                      setShowGRBE(value);
                    }}
                  />
                </div>
              </div>
            )}

            {showGRBE && showGR && (
              <div className="">
                <table
                  className="table ap-px-spacing-4"
                  style={{ width: "100%" }}
                >
                  <thead st>
                    <tr>
                      <th className="" style={{ textAlign: "left" }} key={1}>
                        <span>Investor group</span>
                        <CustomTooltipIcon
                          tooltipText={tooltips.investorGroup}
                        />
                      </th>
                      <th className="col-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {investorTable?.map((data, index) => {
                      return (
                        <tr key={index}>
                          <td key={1}>
                            <Input
                              className="col"
                              value={data["investor_group"]}
                              title="Please provide investor name"
                              onChange={(evnt) => {
                                tableChange(index, evnt, "investor_group");
                              }}
                              error={duplicateInvestorErrors[index]}
                            />
                          </td>
                          <td>
                            <Button
                              className="btn btn-outline-danger"
                              onClick={(e) =>
                                deleteTableRows(e, index, "investor_group")
                              }
                            >
                              <span className="Appkit4-icon icon-close-outline"></span>
                            </Button>
                          </td>
                          <td></td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td>
                        <Button
                          className="col"
                          kind="secondary"
                          onClick={() => addTableRows("investor")}
                        >
                          Add another investor
                        </Button>
                      </td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>

                <div className="row">
                  <div className="d-flex flex-row col ap-pr-spacing-0">
                    <p className="ap-mt-spacing-2">Blended group ratio</p>
                    <CustomTooltipIcon
                      tooltipText={tooltips.blendedGroupRatio}
                    />
                  </div>
                  <div className="col">
                    <Input
                      value={blendedGroupRatio}
                      error={
                        blendedGroupRatio &&
                        !blendedGroupRatio?.match(/^[3-9][0-9]$|^100$/)
                      }
                      onChange={(value) => setBlendedGroupRatio(value)}
                      suffix={"%"}
                    />
                  </div>
                </div>
                <div className="row">
                  <p className="col">Blended net group-interest expense</p>
                  <Input
                    className="col"
                    type="text"
                    min={0}
                    value={formatNumber(blendedNetGroupInterestExpense)}
                    onChange={handleInputChange}
                    onFocus={() => {
                      // Ensure that the value is set to a number when the input is focused
                      if (blendedNetGroupInterestExpense === "") {
                        setBlendedNetGroupInterestExpense(0);
                      }
                    }}
                  />
                </div>
              </div>
            )}

            <table className="table ap-px-spacing-4" style={{ width: "100%" }}>
              <thead st>
                <tr>
                  <th style={{ textAlign: "left" }}>
                    <span>
                      Interest allowance (non-consolidated investment) election
                    </span>
                    <CustomTooltipIcon
                      tooltipText={tooltips.nonConsolidatedInvestmentElection}
                    />
                    <Switch
                      style={{ width: "fit-content" }}
                      showIndicator
                      defaultChecked={interestAllowanceElection}
                      onChange={(value) => {
                        setInterestAllowanceElection(value);
                      }}
                    />
                  </th>
                  <th className="col-1"></th>
                </tr>
              </thead>
              {interestAllowanceElection && (
                <tbody>
                  {investmentsTable?.map((data, index) => {
                    return (
                      <tr key={index}>
                        <td>
                          <Input
                            className="col"
                            value={data["investment_name"]}
                            title="Please provide investment name"
                            onChange={(evnt) => {
                              tableChange(index, evnt, "investment_name");
                            }}
                            error={duplicateInvestmentsErrors[index]}
                          />
                        </td>
                        <td>
                          <Button
                            className="btn btn-outline-danger"
                            onClick={(e) =>
                              deleteTableRows(e, index, "investment_name")
                            }
                          >
                            <span className="Appkit4-icon icon-close-outline"></span>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td>
                      <Button
                        className="col"
                        kind="secondary"
                        onClick={() => addTableRows("investment")}
                      >
                        Add another investment
                      </Button>
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                </tbody>
              )}
            </table>
          </Panel>

          {/* Irrevocable Elections section */}

          <Panel title="Irrevocable Elections" className="mt-4">
            <p>
              The following elections are irrevocable for the Group once made.
              Please select all elections that have been made by the Group in
              any period before the first period for which the model is used:
            </p>
            <div className="row vertical-align">
              <p className="col-4">
                Apply the Disregard Regulations to pre-1 April 2020 derivative
                contracts
              </p>
              <CustomTooltipIcon
                tooltipText={tooltips.applyDisregardRegulations}
              />
              <Switch
                disabled={
                  isElectionDisabled("disregard_regulations_election") ||
                  processDateJS(period_start)?.isAfter(
                    processDateJS(new Date("2018-04-01T00:00:00.000Z")),
                  )
                }
                onChange={(e) => {
                  setIrrevocableElections((state) => ({
                    ...state,
                    disregardRegulations: {
                      ...state.disregardRegulations,
                      switch: e,
                      date: period_end,
                      changed: true,
                    },
                  }));
                }}
                className="col-1 justify-center"
                showIndicator
                checked={
                  irrevocableElections?.disregardRegulations?.changed
                    ? irrevocableElections?.disregardRegulations?.switch
                    : isElectionTurnedOn("disregard_regulations_election")
                }
              />
              <Input
                className="col"
                onChange={(e) => {
                  setIrrevocableElections((state) => ({
                    ...state,
                    disregardRegulations: {
                      ...state.disregardRegulations,
                      comment: e,
                    },
                  }));
                }}
                disabled={
                  isElectionDisabled("disregard_regulations_election") ||
                  processDateJS(period_start)?.isAfter(
                    processDateJS(new Date("2018-04-01T00:00:00.000Z")),
                  )
                }
                title="Comment"
                value={irrevocableElections?.disregardRegulations?.comment}
              />
            </div>
            {true && (
              <>
                <div className="row vertical-align">
                  <p className="col-4">
                    Group EBITDA (chargeable gains) election
                  </p>
                  <CustomTooltipIcon tooltipText={tooltips.groupEBITDA} />
                  <Switch
                    disabled={isElectionDisabled("group_ebitda_gains")}
                    onChange={(e) => {
                      setIrrevocableElections((state) => ({
                        ...state,
                        EBITDA: {
                          ...state.EBITDA,
                          switch: e,
                          date: period_end,
                          changed: true,
                        },
                      }));
                    }}
                    className="col-1 justify-center"
                    showIndicator
                    checked={
                      irrevocableElections?.EBITDA?.changed
                        ? irrevocableElections?.EBITDA?.switch
                        : isElectionTurnedOn("group_ebitda_gains")
                    }
                  />
                  <Input
                    className="col"
                    onChange={(e) => {
                      setIrrevocableElections((state) => ({
                        ...state,
                        EBITDA: {
                          ...state.EBITDA,
                          comment: e,
                        },
                      }));
                    }}
                    disabled={isElectionDisabled("group_ebitda_gains")}
                    title="Comment"
                    value={irrevocableElections?.EBITDA?.comment}
                  />
                </div>
              </>
            )}
            <div className="row vertical-align">
              <p className="col-4">
                Interest allowance (alternative calculation) election
              </p>
              <CustomTooltipIcon
                tooltipText={tooltips.alternativeCalculationElection}
              />
              <Switch
                disabled={isElectionDisabled("interest_allowance_alt_calc")}
                onChange={(e) => {
                  setIrrevocableElections((state) => ({
                    ...state,
                    alternativeCalculation: {
                      ...state.alternativeCalculation,
                      switch: e,
                      date: period_end,
                      changed: true,
                    },
                  }));
                }}
                className="col-1 justify-center"
                showIndicator
                checked={
                  irrevocableElections?.alternativeCalculation?.changed
                    ? irrevocableElections?.alternativeCalculation?.switch
                    : isElectionTurnedOn("interest_allowance_alt_calc")
                }
              />
              <Input
                className="col"
                disabled={isElectionDisabled("interest_allowance_alt_calc")}
                onChange={(e) => {
                  setIrrevocableElections((state) => ({
                    ...state,
                    alternativeCalculation: {
                      ...state.alternativeCalculation,
                      comment: e,
                    },
                  }));
                }}
                title="Comment"
                value={irrevocableElections?.alternativeCalculation?.comment}
              />
            </div>
            <div className="row vertical-align">
              <p className="col-4">
                Interest allowance (consolidated partnership) election
              </p>
              <CustomTooltipIcon
                tooltipText={tooltips.consolidatedPartnershipElection}
              />
              <Switch
                disabled={isElectionDisabled(
                  "interest_allowance_consolidated_partnership",
                )}
                onChange={(e) => {
                  setIrrevocableElections((state) => ({
                    ...state,
                    consolidatedPartnership: {
                      ...state.consolidatedPartnership,
                      switch: e,
                      date: period_end,
                      changed: true,
                    },
                  }));
                }}
                className="col-1 justify-center"
                showIndicator
                checked={
                  irrevocableElections?.consolidatedPartnership?.changed
                    ? irrevocableElections?.consolidatedPartnership?.switch
                    : isElectionTurnedOn(
                        "interest_allowance_consolidated_partnership",
                      )
                }
              />
              <Input
                className="col"
                disabled={isElectionDisabled(
                  "interest_allowance_consolidated_partnership",
                )}
                title="Comment"
                value={irrevocableElections?.consolidatedPartnership?.comment}
                onChange={(e) => {
                  setIrrevocableElections((state) => ({
                    ...state,
                    consolidatedPartnership: {
                      ...state.consolidatedPartnership,
                      comment: e,
                    },
                  }));
                }}
              />
            </div>
            {(irrevocableElections?.consolidatedPartnership?.changed
              ? irrevocableElections?.consolidatedPartnership?.switch
              : isElectionTurnedOn(
                  "interest_allowance_consolidated_partnership",
                )) && (
              <ConsolidatedPartnerships
                partnerships={
                  irrevocableElections?.consolidatedPartnership
                    ?.partnerships !== undefined
                    ? irrevocableElections.consolidatedPartnership.partnerships
                    : getGroupData()?.consolidated_partnerships || []
                }
                onChange={(updatedPartnerships) => {
                  const validation = validateConsolidatedPartnerships({
                    partnerships: updatedPartnerships,
                    poaPeriodEndDates:
                      getGroupData()
                        ?.periods_of_account?.map((poa) => poa?.period_end)
                        ?.filter((date) => !!date) || [],
                    firstModelPeriodStartDate:
                      getGroupData()?.model_first_period_start,
                    requireAtLeastOne: true,
                    requireUniqueNames: true,
                  });

                  setConsolidatedPartnershipsStartDateError(
                    validation.hasInvalidStartDate,
                  );
                  setConsolidatedPartnershipsError(validation.hasErrors);

                  setIrrevocableElections((state) => ({
                    ...state,
                    consolidatedPartnership: {
                      ...state.consolidatedPartnership,
                      partnerships: updatedPartnerships,
                      changed: true,
                    },
                  }));
                }}
                canEdit={(partnership) => {
                  // Can edit/delete if electionSubmitted is false (No)
                  const canEditRow = !partnership.electionSubmitted;
                  return canEditRow;
                }}
                defaultDate={period_end}
                isReadOnly={auth?.isReadOnlyUser()}
                showDateColumn={true}
                showDatePicker={false}
                isGroupConfig={false}
                isGroupDataPage={true}
                poaPeriodEndDates={
                  getGroupData()
                    ?.periods_of_account?.map((poa) => poa?.period_end)
                    ?.filter((date) => !!date) || []
                }
                modelFirstPeriodStartDate={
                  getGroupData()?.model_first_period_start
                }
              />
            )}
          </Panel>

          <Panel
            className="mt-2"
            title="Elections altering the period of account or deemed financial statements of the worldwide group"
          >
            <p className="mt-2">
              The group data for the CIR calculations is based on the IAS
              consolidated financial statements of the Worldwide Group for the
              period of account.
            </p>
            <p className="mt-2">
              Where no appropriate consolidated financial statements are
              prepared for the group, various provisions in s481 - s489 TIOPA
              2010 apply to deem financial statements to be prepared for the
              purposes of the CIR calculations. These sections include elections
              to alter the deeming in certain circumstances. Where these
              elections have been made, this can be recorded here. Recording the
              elections here does not alter any of the calculations undertaken
              by the CIR Analyser - they just potentially impact the amounts you
              should include as data input for NGIE, ANGIE, QNGIE and
              Group-EBITDA above.
            </p>

            <div
              className="d-flex flex-row gap-1"
              style={{ alignItems: "center" }}
            >
              <p className="col-4">
                Election not to treat consolidated IAS financial statements as
                deemed to be drawn up for the relevant period:
              </p>
              <CustomTooltipIcon tooltipText={tooltips.electionNotToTreatIAS} />
              <Switch
                className="col justify-center"
                showIndicator
                defaultChecked={electionConsolidatedIAS}
                value={electionConsolidatedIAS}
                onChange={(value) => {
                  if (!value) {
                    setElectionConsolidatedIASDate(undefined);
                    setElectionConsolidatedIASLink("");

                    const validators = recordedElectionsValidators;
                    validators.s484 = true;
                    setRecordedElectionsValidators(validators);
                  }
                  setElectionConsolidatedIAS(value);
                }}
              />
              <CalendarPicker
                disabled={!electionConsolidatedIAS}
                value={electionConsolidatedIASDate}
                onChange={(value, isValid) => {
                  const validators = recordedElectionsValidators;
                  validators.s484 = isValid;
                  setRecordedElectionsValidators(validators);
                  setElectionConsolidatedIASDate(
                    value ? processDate(value) : undefined,
                  );
                }}
                className="col"
                fieldTitle="Date of election (optional)"
                format="DD/MM/YYYY"
                placeholder="dd/mm/yyyy"
                maxDate={electionConsolidatedIASMaxDate}
              />
              <Input
                disabled={!electionConsolidatedIAS}
                value={electionConsolidatedIASLink}
                onChange={(value) => {
                  setElectionConsolidatedIASLink(value);
                }}
                className="col-4"
                title="Link to election (optional)"
              />
            </div>

            <div
              className="d-flex flex-row gap-1"
              style={{ alignItems: "center" }}
            >
              <p className="col-4">
                Election or deemed period of account where no actual financial
                statements drawn up:
              </p>
              <CustomTooltipIcon
                tooltipText={tooltips.electionNoStatementsDrawnUp}
              />
              <Switch
                className="col justify-center"
                showIndicator
                defaultChecked={electionNoStatementsDrawnUp}
                value={electionNoStatementsDrawnUp}
                onChange={(value) => {
                  if (!value) {
                    setElectionNoStatementsDrawnUpDate(undefined);
                    setElectionNoStatementsDrawnUpLink("");

                    const validators = recordedElectionsValidators;
                    validators.s486 = true;
                    setRecordedElectionsValidators(validators);
                  }
                  setElectionNoStatementsDrawnUp(value);
                }}
              />
              <CalendarPicker
                disabled={!electionNoStatementsDrawnUp}
                value={electionNoStatementsDrawnUpDate}
                onChange={(value, isValid) => {
                  const validators = recordedElectionsValidators;
                  validators.s486 = isValid;
                  setElectionNoStatementsDrawnUpDate(
                    value ? processDate(value) : undefined,
                  );
                  setRecordedElectionsValidators(validators);
                }}
                className="col"
                fieldTitle="Date of election (optional)"
                format="DD/MM/YYYY"
                placeholder="dd/mm/yyyy"
                maxDate={electionNoStatementsDrawnUpMaxDate}
              />
              <Input
                disabled={!electionNoStatementsDrawnUp}
                value={electionNoStatementsDrawnUpLink}
                onChange={(value) => setElectionNoStatementsDrawnUpLink(value)}
                className="col-4"
                title="Link to election (optional)"
              />
            </div>
          </Panel>

          <div className="container-fluid mt-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <BackButton disabled={isPosting} />
              </div>
              <div className="d-flex gap-2">
                <Button
                  hiddenInReadonly
                  loading={isPosting}
                  disabled={isError}
                  onClick={() => save()}
                  kind="secondary"
                >
                  Save
                </Button>
                <NextButton
                  loading={isPosting}
                  disabled={isError || disableViewModeProgress}
                  preNavigation={save}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GroupData;
